import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Heart, ShoppingCart, Minus, Plus, Send } from 'lucide-react';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { StarRating } from '../components/StarRating';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const { addToCart, addToWishlist, isInWishlist } = useCart();
  const { user } = useAuth();

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ totalReviews: 0, averageRating: 0 });
  const [reviewForm, setReviewForm] = useState({ userName: '', userEmail: '', rating: 0, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);

  useEffect(() => {
    if (user) {
      setReviewForm(prev => ({ ...prev, userName: user.name || '', userEmail: user.email || '' }));
    }
  }, [user]);

  useEffect(() => {
    fetchProduct();
    fetchReviews();
    // Auto-refresh reviews every 15 seconds for live updates
    const interval = setInterval(fetchReviews, 15000);
    return () => clearInterval(interval);
  }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await axios.get(`${API}/products/${id}`);
      setProduct(response.data);
      setQuantity(response.data.moq || 1);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/reviews/product/${id}`);
      setReviews(response.data.reviews);
      setReviewStats({ totalReviews: response.data.totalReviews, averageRating: response.data.averageRating });
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (reviewForm.rating === 0) { toast.error('Please select a star rating'); return; }
    if (!reviewForm.userName.trim()) { toast.error('Please enter your name'); return; }
    if (!reviewForm.comment.trim()) { toast.error('Please write a review'); return; }
    setSubmittingReview(true);
    try {
      await axios.post(`${API}/reviews`, {
        ...reviewForm,
        productId: id,
        userId: user?.uid || null
      });
      toast.success('Review submitted!');
      setReviewForm({ userName: user?.name || '', userEmail: user?.email || '', rating: 0, title: '', comment: '' });
      fetchReviews();
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleAddToCart = () => {
    addToCart(product, quantity);
    toast.success(`Added ${quantity} item(s) to cart!`);
  };

  const handleBuyNow = () => {
    addToCart(product, quantity);
    navigate('/cart');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#D4AF37] border-t-transparent"></div>
      </div>
    );
  }

  if (!product) return null;

  // Build gallery: images array or fallback to single image
  const allImages = (product.images && product.images.length > 0) ? product.images : [product.image];

  return (
    <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Image Gallery */}
        <div data-testid="product-image-container">
          <div className="aspect-square overflow-hidden bg-gray-100 mb-3">
            <img src={allImages[selectedImageIdx]} alt={product.name} className="w-full h-full object-cover transition-opacity" data-testid="product-main-image" />
          </div>
          {allImages.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-2" data-testid="product-image-thumbnails">
              {allImages.map((img, idx) => (
                <button key={idx} type="button" onClick={() => setSelectedImageIdx(idx)}
                  className={`w-16 h-16 flex-shrink-0 overflow-hidden border-2 transition-colors ${idx === selectedImageIdx ? 'border-[#D4AF37]' : 'border-gray-200 hover:border-gray-400'}`}
                  data-testid={`thumbnail-${idx}`}>
                  <img src={img} alt={`View ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Details */}
        <div>
          <p className="text-sm text-[#D4AF37] mb-2 uppercase tracking-wide">{product.category}</p>
          <h1 className="text-3xl md:text-4xl font-bold mb-2" style={{fontFamily: 'Playfair Display, serif'}} data-testid="product-name">
            {product.name}
          </h1>

          {/* Rating summary */}
          {reviewStats.totalReviews > 0 && (
            <div className="flex items-center gap-2 mb-4" data-testid="product-rating-summary">
              <StarRating rating={Math.round(reviewStats.averageRating)} size={18} />
              <span className="text-sm text-gray-600">{reviewStats.averageRating} ({reviewStats.totalReviews} review{reviewStats.totalReviews !== 1 ? 's' : ''})</span>
            </div>
          )}
          
          <div className="flex items-center gap-3 mb-6">
            <span className="text-3xl font-bold text-[#D4AF37]" data-testid="product-price">₹{product.price}</span>
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="text-xl text-gray-400 line-through">₹{product.originalPrice}</span>
            )}
            {product.originalPrice && product.originalPrice > product.price && (
              <span className="bg-green-100 text-green-800 px-3 py-1 text-sm font-medium">
                {Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
              </span>
            )}
          </div>

          <p className="text-gray-600 leading-relaxed mb-8" data-testid="product-description">
            {product.description}
          </p>

          {/* Stock & Sold */}
          <div className="mb-6 space-y-1">
            <p className="text-sm">
              <span className="font-medium">Availability:</span>{' '}
              <span className={product.stock > 0 ? 'text-green-600' : 'text-red-600'}>
                {product.stock > 0 ? `In Stock (${product.stock} left)` : 'Out of Stock'}
              </span>
            </p>
            {product.soldCount > 0 && (
              <p className="text-sm text-gray-500" data-testid="product-sold-count">{product.soldCount} sold</p>
            )}
            {product.moq > 1 && (
              <p className="text-sm text-amber-600" data-testid="product-moq">Minimum order: {product.moq} units</p>
            )}
          </div>

          {/* Quantity */}
          <div className="mb-8">
            <label className="block text-sm font-medium mb-2">Quantity</label>
            <div className="flex items-center gap-4">
              <button onClick={() => setQuantity(Math.max(product.moq || 1, quantity - 1))} className="p-2 border border-gray-300 hover:bg-gray-50" data-testid="decrease-quantity-btn">
                <Minus size={18} />
              </button>
              <span className="text-lg font-medium w-12 text-center" data-testid="quantity-display">{quantity}</span>
              <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="p-2 border border-gray-300 hover:bg-gray-50" data-testid="increase-quantity-btn">
                <Plus size={18} />
              </button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-4">
            <Button onClick={handleBuyNow} className="bg-[#D4AF37] text-white hover:bg-[#c9a22e] flex-1 px-8 py-3 h-auto" disabled={product.stock === 0} data-testid="buy-now-btn">
              <ShoppingCart className="mr-2" size={20} /> Buy Now
            </Button>
            <Button onClick={handleAddToCart} className="border border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white bg-transparent flex-1 px-8 py-3 h-auto" disabled={product.stock === 0} data-testid="add-to-cart-detail-btn">
              Add to Cart
            </Button>
            <Button onClick={() => { addToWishlist(product); toast.success('Added to wishlist!'); }} className="border border-[#0A192F] text-[#0A192F] hover:bg-[#0A192F] hover:text-white bg-transparent" data-testid="add-to-wishlist-btn">
              <Heart size={20} className={isInWishlist(product.id) ? 'fill-red-500 text-red-500' : ''} />
            </Button>
          </div>
        </div>
      </div>

      {/* ========== REVIEWS SECTION ========== */}
      <div className="mt-16 border-t pt-12" data-testid="reviews-section">
        <h2 className="text-2xl font-bold mb-8" style={{fontFamily: 'Playfair Display, serif'}}>Customer Reviews</h2>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Left: Rating Overview + Write Review */}
          <div className="lg:col-span-1 space-y-8">
            {/* Rating Overview */}
            <div className="bg-[#F8F9FA] p-6 rounded-lg" data-testid="rating-overview">
              <div className="text-center mb-4">
                <p className="text-5xl font-bold text-[#0A192F]">{reviewStats.averageRating || '0.0'}</p>
                <StarRating rating={Math.round(reviewStats.averageRating)} size={22} className="justify-center mt-2" />
                <p className="text-sm text-gray-500 mt-1">{reviewStats.totalReviews} review{reviewStats.totalReviews !== 1 ? 's' : ''}</p>
              </div>
            </div>

            {/* Write Review Form */}
            <div className="bg-white border border-gray-200 p-6 rounded-lg" data-testid="write-review-form">
              <h3 className="font-bold text-lg mb-4">Write a Review</h3>
              <form onSubmit={handleSubmitReview} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Your Rating *</label>
                  <StarRating rating={reviewForm.rating} size={28} interactive onChange={(r) => setReviewForm({...reviewForm, rating: r})} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Your Name *</label>
                  <Input value={reviewForm.userName} onChange={(e) => setReviewForm({...reviewForm, userName: e.target.value})} placeholder="John Doe" data-testid="review-name-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Email (optional)</label>
                  <Input type="email" value={reviewForm.userEmail} onChange={(e) => setReviewForm({...reviewForm, userEmail: e.target.value})} placeholder="john@example.com" data-testid="review-email-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Review Title</label>
                  <Input value={reviewForm.title} onChange={(e) => setReviewForm({...reviewForm, title: e.target.value})} placeholder="Great product!" data-testid="review-title-input" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Your Review *</label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    placeholder="Tell us what you think about this product..."
                    data-testid="review-comment-input"
                  />
                </div>
                <Button type="submit" disabled={submittingReview} className="w-full bg-[#D4AF37] text-white hover:bg-[#c9a22e]" data-testid="submit-review-btn">
                  <Send size={16} className="mr-2" /> {submittingReview ? 'Submitting...' : 'Submit Review'}
                </Button>
              </form>
            </div>
          </div>

          {/* Right: Review List */}
          <div className="lg:col-span-2">
            {reviews.length === 0 ? (
              <div className="text-center py-16 text-gray-400" data-testid="no-reviews-message">
                <p className="text-lg font-medium">No reviews yet</p>
                <p className="text-sm mt-1">Be the first to review this product!</p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b border-gray-100 pb-6" data-testid={`review-${review.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-[#0A192F]">{review.userName}</span>
                          {review.verified && <span className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium">Verified</span>}
                        </div>
                        <StarRating rating={review.rating} size={14} />
                      </div>
                      <span className="text-xs text-gray-400">{new Date(review.createdAt).toLocaleDateString()}</span>
                    </div>
                    {review.title && <p className="font-medium mt-2">{review.title}</p>}
                    <p className="text-gray-600 text-sm mt-1 leading-relaxed">{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
